"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { useAuth } from "@/lib/auth/AuthProvider";
import { LoginSkeleton } from "@/components/ui/Skeleton";
import { LanguageSelector } from "@/app/_components/login/LanguageSelector";
import { LoginView } from "@/app/_components/login/LoginView";
import { ForgotPasswordView } from "@/app/_components/login/ForgotPasswordView";
import { EmailSentView } from "@/app/_components/login/EmailSentView";
import { ResetPasswordView } from "@/app/_components/login/ResetPasswordView";
import { StatusView } from "@/app/_components/login/StatusView";
import type { LoginViewKey } from "@/app/_components/login/types";

// Hoja de vidrio centrada (DISENO.md §9): 420 px, radio 24, relleno de 28 px (40 px desde `sm`).
const SHEET = "glass-strong w-full max-w-[420px] rounded-sheet p-7 sm:p-10";

export default function LoginPage() {
  const { t } = useI18n();
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const [view, setView] = useState<LoginViewKey>("login");

  useEffect(() => {
    if (isAuthenticated) {
      router.replace("/dashboard");
    }
  }, [isAuthenticated, router]);

  const showSupport = view === "login" || view === "forgot_step1";

  return (
    <div className="relative flex min-h-dvh w-full flex-col items-center overflow-x-hidden">
      <header className="z-10 flex w-full shrink-0 items-center justify-between px-4 py-4 sm:px-8 sm:py-6 lg:px-16">
        {/* Logo a 44 px sobre placa blanca: a 36 px no se leía, y en oscuro "ELEC" (azul marino) se perdía.
            Radio anidado: 20 − 6 = 14. */}
        <span className="glass flex items-center rounded-card p-1.5">
          <span className="flex items-center rounded-[14px] bg-white px-2 py-1">
            <Image
              src="/seaelec-logo.png"
              alt={t("login.logoAlt")}
              width={140}
              height={110}
              priority
              className="h-11 w-auto object-contain"
            />
          </span>
        </span>
        <div className="glass flex items-center rounded-pill p-1">
          <LanguageSelector />
        </div>
      </header>

      <main className="z-10 flex w-full flex-1 items-center justify-center px-4 py-8">
        {/* Con la sesión abierta se redirige al dashboard; mientras tanto, la hoja muestra su esqueleto en el mismo lugar. */}
        {isAuthenticated ? (
          <LoginSkeleton className={SHEET} />
        ) : (
          // `key` reinicia la entrada de la hoja al cambiar de vista: cada paso del flujo entra igual.
          <section key={view} className={`${SHEET} animate-sheet-in`}>
            {view === "login" && <LoginView />}
            {view === "forgot_step1" && <ForgotPasswordView setView={setView} />}
            {view === "forgot_step2" && <EmailSentView setView={setView} />}
            {view === "forgot_step3" && <ResetPasswordView setView={setView} />}
            {view === "forgot_success" && (
              <StatusView
                type="success"
                title={t("login.successTitle")}
                text={t("login.successText")}
                btnText={t("login.goToLogin")}
                onAction={() => setView("login")}
              />
            )}
            {view === "forgot_error" && (
              <StatusView
                type="error"
                title={t("login.errorTitle")}
                text={t("login.errorText")}
                btnText={t("login.requestNewLink")}
                onAction={() => setView("forgot_step1")}
              />
            )}
          </section>
        )}
      </main>

      <footer className="z-10 flex w-full shrink-0 flex-col items-center gap-4 px-4 pb-8 text-center">
        {showSupport && (
          <p className="flex flex-col items-center gap-1 text-sm text-muted-foreground">
            {t("login.supportQuestion")}
            <a
              href="mailto:soporte@seaelec.cl"
              className="rounded-[4px] font-medium text-brand-primary transition-opacity duration-200 hover:opacity-75 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30"
            >
              soporte@seaelec.cl
            </a>
          </p>
        )}
        <p className="text-xs text-muted-foreground">{t("login.copyright")}</p>
      </footer>
    </div>
  );
}
